import { filesize } from 'filesize'

export enum FileSizeUnit {
   bytes = 'Bytes',
   kilobytes = 'KB',
   megabytes = 'MB',
}

/**
 * Array of file sizes in ascending size order
 */
const ascendingFileSizeUnits = [
   FileSizeUnit.bytes,
   FileSizeUnit.kilobytes,
   FileSizeUnit.megabytes,
] as const

export type FileSizeUnitSmall = "B" | "KB" | "MB"

/**
 * Converts a numeric file size from one file size unit to another
 * @param from FileSizeUnit
 * @param to FileSizeUnit
 * @param value number
 * @returns number
 * 
 * @example
 * const result = convertFileSizeUnit(FileSizeUnit.megabytes, FileSizeUnit.bytes, 10)
 */
export function convertFileSizeUnit(params: { from?: FileSizeUnit, to: FileSizeUnit, value: number }): number {
   const sizes = ascendingFileSizeUnits
   const fromPosition = sizes.indexOf(params.from || FileSizeUnit.bytes)
   const toPosition = sizes.indexOf(params.to)
   const multiple = Math.abs(fromPosition - toPosition)
   const multiplier = Math.pow(1000, multiple)

   if (fromPosition < toPosition) {
      return params.value / multiplier
   } else {
      if (fromPosition > toPosition) {
         return params.value * multiplier
      }
   }
   return params.value
}

// Quickly convert to bytes
export const inBytes = (value: number, from: FileSizeUnit | FileSizeUnitSmall): number => {
   switch (from) {
      case 'B':
      case FileSizeUnit.bytes:
         return value
      case FileSizeUnit.kilobytes:
         return value * 1000
      case FileSizeUnit.megabytes:
         return value * 1000 * 1000
   }
   throw new Error('Invalid file size unit')
}

export const inMB = (value: number, from: FileSizeUnit | FileSizeUnitSmall): number => {
   switch (from) {
      case 'B':
      case FileSizeUnit.bytes:
         return value / 1000 / 1000
      case FileSizeUnit.kilobytes:
         return value / 1000
      case FileSizeUnit.megabytes:
         return value
   }
   throw new Error('Invalid file size unit')
}

export function formatByteSize(bytes: number) {
   return filesize(bytes, { output: 'string', standard: 'jedec' }).replace(' ', '')
}